import { useEffect, useMemo, useState } from 'react';

import { Box, DrawerOverlay, Flex, Spinner } from '@chakra-ui/react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RRule } from 'rrule';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';
import { createOneOnOneApi } from 'screens/oneOnOneRabbit/InstancePage/apis';
import { oneOnOneLandingPageReactQueryKeys } from 'screens/oneOnOneRabbit/landingPage/constants/oneOnOneReactQueryKeys';
import useRouteQueryParams from 'screens/performanceReview/hooks/useRouteQueryParams';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { OneOnOneIconDualTone } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';
import { sendClickedAddedOnOneOnOne } from 'constants/mixpanelHelper';
import { isNetchexLogin } from 'constants/netChexConstants';
import { routes } from 'constants/routes';

import {
  BottomDrawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  primaryDrawerWidth,
  secondaryDrawerWidth,
} from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextHeader3, TextLabel } from 'uiToolkitV1/Typography';

import CalendarSyncSection from './CalendarSyncSection';
import { MeetingsDetails } from './MeetingsDetails';
import ParticipantSelectionComp from './ParticipantSelectionComp';
import { useExistingOneOnOneList } from './hooks/dataHooks';
import { closeCreateOneOnOneDrawer, useCreateOneOnOneDrawerData } from './slice/CreateOneOnOneDrawerSlice';

const StyledDrawerBody = styled(DrawerBody)`
  overflow-x: hidden;
`;

const Header = ({ onClose }) => {
  const theme = useTheme();
  return (
    <DrawerHeader onClose={onClose}>
      <Box py={3} pl={6}>
        <Flex>
          <OneOnOneIconDualTone />
          <TextLabel m="1px 0px 0px 1px" color="#666666">
            1:1
          </TextLabel>
        </Flex>
        <TextHeader3 color={theme.colors.text.g1} bold>
          Create 1:1
        </TextHeader3>
      </Box>
    </DrawerHeader>
  );
};

const Footer = ({ onClose, onSubmit, disableSubmit, isLoading, isOneOnOneAlreadySetup, employeeMiniProfile }) => {
  const theme = useTheme();
  const { startDateTime: selectedDate, frequency: selectedFrequency } = useCreateOneOnOneDrawerData();

  const recurrenceData = RRule.fromString(selectedFrequency ?? '');
  const oneOnOneSetupDetails = recurrenceData.toText();

  return (
    <DrawerFooter
      borderTopWidth="1px"
      h="72px"
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        top: 'auto',
      }}
      backgroundColor={theme.colors.neutral.F5F7F9}
    >
      <Button variant="tertiary" mr={isMobileView() ? '0px' : '406px'} onClick={onClose}>
        Cancel
      </Button>
      {isOneOnOneAlreadySetup ? (
        <Tooltip
          placement="top-start"
          label={`Your 1:1 with ${employeeMiniProfile?.displayName} occurs ${oneOnOneSetupDetails}`}
        >
          <Box>
            <Button variant="filled" colorScheme="blue" onClick={onSubmit} disabled={isOneOnOneAlreadySetup} w="73px">
              Create
            </Button>
          </Box>
        </Tooltip>
      ) : (
        <Button variant="filled" colorScheme="blue" onClick={onSubmit} disabled={disableSubmit || isLoading} w="73px">
          {isLoading ? (
            <Flex alignItems="center">
              <Spinner size="md" color="white" />
            </Flex>
          ) : (
            'Create'
          )}
        </Button>
      )}
    </DrawerFooter>
  );
};

export const generateProfilePageDataAsMiniProfile = (profilePageData) => {
  const { displayName, id, profilePhotoSrc, designation, employeeFunction } = profilePageData;
  return {
    id,
    displayName,
    profilePhoto: profilePhotoSrc,
    designation,
    employeeFunction,
    ownerType: null,
  };
};

const CreateOneOnOneDrawer = ({ isSecondaryPanelOpen = false, HeaderComp = Header, FooterComp = Footer }) => {
  const theme = useTheme();
  const { employeeId: profilePageId } = useRouteQueryParams(routes.profile);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const toast = useToast();
  const {
    isOpen,
    employeeId,
    employeeMiniProfile,
    excludedParticipantIds,
    startDateTime,
    duration,
    frequency,
    participantId,
    isCalendarSynced,
    relation,
    onCreateOneOnOne,
  } = useCreateOneOnOneDrawerData();

  const currentLoggedInEmployeeId = useSelector(selectLoggedInEmployeeId) ?? {};
  const { data: existingEmployeesList, isLoading: isExistingEmployeeListLoading } = useExistingOneOnOneList();

  const { data: profilePageData } = useProfilePage({
    employeeId: profilePageId,
  });

  const profilePageDataAsMiniProfile = useMemo(
    () => (profilePageData ? generateProfilePageDataAsMiniProfile(profilePageData) : {}),
    [profilePageData]
  );

  const disabledEmployeeIds = existingEmployeesList?.entity?.map((item) => item);
  const numberEmployeeId = parseInt(profilePageDataAsMiniProfile?.id);
  const isOneOnOneAlreadySetup = disabledEmployeeIds?.includes(numberEmployeeId);

  const refetchHandler = () => {
    queryClient.invalidateQueries([oneOnOneLandingPageReactQueryKeys.oneOnOneLandingPageDetails]);
    queryClient.invalidateQueries([oneOnOneLandingPageReactQueryKeys.oneOnOneActiveArchiveCount]);
  };

  const isNotLoggedInEmployee = profilePageId && profilePageId !== currentLoggedInEmployeeId;

  // if profile page is open and the profile page is not of the logged in employee
  // then use the profile page data as mini profile
  // else use the employee mini profile from the context
  const employeeMiniProfileForProps =
    profilePageId && isNotLoggedInEmployee ? profilePageDataAsMiniProfile : employeeMiniProfile;

  const onClose = () => {
    dispatch(closeCreateOneOnOneDrawer());
  };

  const onSave = async () => {
    try {
      setIsLoading(true);
      const res = await createOneOnOneApi({
        employeeId: participantId,
        startTimeTs: startDateTime.valueOf() / 1000,
        duration,
        recurrenceRule: frequency,
      });

      refetchHandler();
      if (onCreateOneOnOne) onCreateOneOnOne();

      toast({
        title: '1:1 created successfully.',
        status: 'success',
      });
    } catch (err) {
      console.error("Couldn't create 1:1", err);
      toast({
        title: 'Something went wrong while creating 1:1. Please try again.',
        status: 'error',
      });
    } finally {
      sendClickedAddedOnOneOnOne({
        eventProperties: {
          frequency,
          relationship: relation,
        },
      });
      onClose();

      setIsLoading(false);
    }
  };
  const mobileViewProps = isMobileView() ? { marginLeft: '0px', marginRight: '0px' } : {};

  return (
    <BottomDrawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent
        width={isSecondaryPanelOpen ? `${secondaryDrawerWidth}px` : `${primaryDrawerWidth}px`}
        {...mobileViewProps}
      >
        <HeaderComp onClose={onClose} />
        <StyledDrawerBody px={2}>
          <Box backgroundColor={theme.colors.neutral.EBEFF3} borderRadius="8px" p={1}>
            <ParticipantSelectionComp
              excludedParticipantIds={excludedParticipantIds}
              employeeMiniProfile={employeeMiniProfileForProps}
            />
            {!isNetchexLogin && <CalendarSyncSection />}
            <MeetingsDetails />
          </Box>
        </StyledDrawerBody>
        <FooterComp
          onClose={onClose}
          onSubmit={onSave}
          disableSubmit={!participantId}
          isLoading={isLoading}
          isOneOnOneAlreadySetup={isOneOnOneAlreadySetup}
          employeeMiniProfile={employeeMiniProfileForProps}
        />
      </DrawerContent>
    </BottomDrawer>
  );
};

export { CreateOneOnOneDrawer };
